
import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Bell, BellRing, X, User, AlertCircle, Zap } from "lucide-react";
import { AttendanceRecord } from "@/services/attendanceService";
import { Class } from "@/types/classTypes";
import { Badge } from "@/components/ui/badge";

interface AttendanceAlertProps {
  classData: Class;
  attendanceRecords: AttendanceRecord[];
  onSendReminder?: (studentIds: string[]) => Promise<void>;
}

const AttendanceAlerts: React.FC<AttendanceAlertProps> = ({ 
  classData, 
  attendanceRecords,
  onSendReminder 
}) => {
  const [consecutiveAbsences, setConsecutiveAbsences] = useState<Record<string, number>>({});
  const [lowAttendanceStudents, setLowAttendanceStudents] = useState<Record<string, number>>({});
  const [isReminding, setIsReminding] = useState(false);
  
  useEffect(() => {
    // Group by student
    const recordsByStudent: Record<string, AttendanceRecord[]> = {};
    
    attendanceRecords.forEach(record => {
      if (!recordsByStudent[record.student_id]) {
        recordsByStudent[record.student_id] = [];
      }
      recordsByStudent[record.student_id].push(record);
    });
    
    // Check for consecutive absences
    const absences: Record<string, number> = {};
    const attendance: Record<string, number> = {};
    
    Object.entries(recordsByStudent).forEach(([studentId, records]) => {
      // Sort records by date (newest first)
      const sortedRecords = [...records].sort((a, b) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      
      // Count consecutive absences
      let consecutive = 0;
      for (const record of sortedRecords) {
        if (record.status === 'absent') {
          consecutive++;
        } else {
          break;
        }
      }
      
      if (consecutive >= 2) {
        absences[studentId] = consecutive;
      }
      
      // Calculate attendance percentage
      const totalSessions = records.length;
      const presentCount = records.filter(r => r.status === 'present').length;
      const attendanceRate = totalSessions > 0 ? (presentCount / totalSessions) * 100 : 0;
      
      if (attendanceRate < 70 && totalSessions >= 3) {
        attendance[studentId] = Math.round(attendanceRate);
      }
    });
    
    setConsecutiveAbsences(absences);
    setLowAttendanceStudents(attendance);
  }, [attendanceRecords]);
  
  const handleSendReminders = async () => {
    if (!onSendReminder) return;
    
    setIsReminding(true);
    try {
      const studentIds = [...Object.keys(consecutiveAbsences), ...Object.keys(lowAttendanceStudents)];
      // Remove duplicates
      const uniqueStudentIds = [...new Set(studentIds)];
      await onSendReminder(uniqueStudentIds);
    } catch (error) {
      console.error("Error sending reminders:", error);
    } finally {
      setIsReminding(false);
    }
  };
  
  const consecutiveCount = Object.keys(consecutiveAbsences).length;
  const lowAttendanceCount = Object.keys(lowAttendanceStudents).length;
  
  if (consecutiveCount === 0 && lowAttendanceCount === 0) {
    return null;
  }

  return (
    <div className="space-y-4 mb-6">
      {consecutiveCount > 0 && (
        <Alert variant="destructive" className="bg-red-50 text-red-800 border-red-200">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Consecutive Absences Detected</AlertTitle>
          <AlertDescription>
            <div className="mt-2">
              <p className="mb-2">
                {consecutiveCount} {consecutiveCount === 1 ? 'student has' : 'students have'} missed multiple classes in a row.
              </p>
              <div className="flex flex-wrap gap-2 mt-1">
                {onSendReminder && (
                  <Button 
                    size="sm" 
                    variant="destructive"
                    className="mt-1"
                    onClick={handleSendReminders}
                    disabled={isReminding}
                  >
                    <BellRing className="mr-1.5 h-3.5 w-3.5" />
                    Send Attendance Reminder
                  </Button>
                )}
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}
      
      {lowAttendanceCount > 0 && (
        <Alert variant="warning" className="bg-amber-50 text-amber-800 border-amber-200">
          <Zap className="h-4 w-4" />
          <AlertTitle>Low Attendance Alert</AlertTitle>
          <AlertDescription>
            <div className="mt-2">
              <p className="mb-2">
                {lowAttendanceCount} {lowAttendanceCount === 1 ? 'student has' : 'students have'} attendance below 70%.
              </p>
              <div className="flex flex-wrap gap-2 mt-1">
                {onSendReminder && !consecutiveCount && (
                  <Button 
                    size="sm" 
                    variant="warning"
                    className="mt-1 bg-amber-600 hover:bg-amber-700"
                    onClick={handleSendReminders}
                    disabled={isReminding}
                  >
                    <Bell className="mr-1.5 h-3.5 w-3.5" />
                    Send Attendance Reminder
                  </Button>
                )}
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default AttendanceAlerts;
